import { apiClient } from './client';
import type {
  ProfileActionDto,
  ProfileActionViewModel,
  ProfileDto,
  ProfileViewModel,
} from '../types/profile';
import { RUNTIME_API_CONFIG } from '../config/runtimeApi';

const PROFILE_PATH = '/api/v1/student/profile';

const fallbackProfileDto: ProfileDto = {
  user: {
    display_name: '学习达人',
    grade_name: '六年级',
    avatar_label: '学',
  },
  summary: {
    streak_days: 7,
    completed_task_count: 16,
    learning_hours: 186,
    mastered_knowledge_count: 23,
    unread_notification_count: 1,
  },
  actions: [
    {
      id: 'wrong-questions',
      title: 'AI错题本',
      description: '5题待复习',
      route_key: 'wrong-questions',
    },
    {
      id: 'study-plan',
      title: '成长计划',
      description: '距期末19天',
      route_key: 'study-plan',
    },
    {
      id: 'weekly-report',
      title: '学习周报',
      description: '查看本周学习总结',
      route_key: 'weekly-report',
    },
    {
      id: 'notifications',
      title: '消息通知',
      description: '有新的学习提醒',
      route_key: 'notifications',
    },
    {
      id: 'learning-records',
      title: '学习记录',
      description: '拍照搜题、讲题和批改历史',
      route_key: 'learning-records',
    },
    {
      id: 'ranking',
      title: '本周榜单',
      description: '查看班级排名和登顶榜',
      route_key: 'ranking',
    },
    {
      id: 'flashcards',
      title: '知识卡片',
      description: '翻卡速记和知识点复习',
      route_key: 'flashcards',
    },
    {
      id: 'settings',
      title: '设置',
      description: '账号、偏好与关于',
      route_key: 'settings',
    },
  ],
};

const safeNumber = (value: number | null): number => {
  if (value === null || Number.isNaN(value)) {
    return 0;
  }
  return value;
};

const mapProfileActionDtoToViewModel = (
  action: ProfileActionDto
): ProfileActionViewModel => ({
  id: action.id,
  title: action.title ?? '未命名入口',
  description: action.description ?? '暂无说明',
  routeKey: action.route_key ?? null,
});

export const mapProfileDtoToViewModel = (dto: ProfileDto): ProfileViewModel => ({
  displayName: dto.user?.display_name ?? '同学',
  gradeName: dto.user?.grade_name ?? '未设置年级',
  avatarLabel: dto.user?.avatar_label ?? '学',
  streakDays: Math.max(0, Math.round(safeNumber(dto.summary?.streak_days ?? null))),
  completedTaskCount: Math.max(0, Math.round(safeNumber(dto.summary?.completed_task_count ?? null))),
  learningHours: Math.max(0, Math.round(safeNumber(dto.summary?.learning_hours ?? null))),
  masteredKnowledgeCount: Math.max(
    0,
    Math.round(safeNumber(dto.summary?.mastered_knowledge_count ?? null))
  ),
  unreadNotificationCount: Math.max(
    0,
    Math.round(safeNumber(dto.summary?.unread_notification_count ?? null))
  ),
  actions: (dto.actions ?? []).map(mapProfileActionDtoToViewModel),
});

export const getProfile = async (): Promise<ProfileViewModel> => {
  if (!RUNTIME_API_CONFIG.enabled) {
    return mapProfileDtoToViewModel(fallbackProfileDto);
  }

  const response = await apiClient.get<ProfileDto>(PROFILE_PATH);
  return mapProfileDtoToViewModel(response.data);
};
